import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Upload, Download, Trash2, Eye, FileText, Image, Film, Palette, Loader2 } from 'lucide-react';
import { Node as BoardNode, Edge as BoardEdge, Attachment, NODE_TYPES } from '../types';
import { storageService } from '../services/storageService';
import toast from 'react-hot-toast';

type BoardTheme = 'light' | 'dark';

interface SidebarProps {
  selectedNode: BoardNode | null;
  selectedEdge: BoardEdge | null;
  theme: BoardTheme;
  onUpdateNode: (node: BoardNode) => void;
  onUpdateEdge: (edge: BoardEdge) => void;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  selectedNode,
  selectedEdge,
  theme,
  onUpdateNode,
  onUpdateEdge,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'properties' | 'attachments' | 'notes'>('properties');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isEditingEdgeNotes, setIsEditingEdgeNotes] = useState(false);
  const [notesContent, setNotesContent] = useState('');
  const [edgeNotesContent, setEdgeNotesContent] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || !selectedNode) return;

    setIsUploading(true);
    const toastId = toast.loading(`Uploading ${files.length} file(s)...`);

    try {
      const newAttachments: Attachment[] = [];

      for (const file of Array.from(files)) {
        const attachmentId = `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const path = `attachments/${selectedNode.id}/${attachmentId}_${file.name}`;

        const downloadUrl = await storageService.uploadFile(file, path);

        const attachment: Attachment = {
          id: attachmentId,
          nodeId: selectedNode.id,
          name: file.name,
          mime: file.type,
          size: file.size,
          objectUrl: downloadUrl
        };
        newAttachments.push(attachment);
      }

      const updatedNode = {
        ...selectedNode,
        attachments: [...selectedNode.attachments, ...newAttachments]
      };
      onUpdateNode(updatedNode);
      toast.success('Upload complete', { id: toastId });
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload files', { id: toastId });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    if (!selectedNode) return;

    // We don't automatically delete from storage here to avoid accidental data loss
    // and because multiple nodes might theoretically link to the same file (though not in current design)
    // Cloud Functions or manual cleanup can handle orphans.

    const updatedNode = {
      ...selectedNode,
      attachments: selectedNode.attachments.filter(a => a.id !== attachmentId)
    };
    onUpdateNode(updatedNode);
  };

  const handleNodePropertyChange = (property: string, value: any) => {
    if (!selectedNode) return;
    const updatedNode = { ...selectedNode, [property]: value };
    onUpdateNode(updatedNode);
  };

  const handleEdgePropertyChange = (property: string, value: any) => {
    if (!selectedEdge) return;
    const updatedEdge = { ...selectedEdge, [property]: value };
    onUpdateEdge(updatedEdge);
  };

  // Helper: extract HH:mm from a timestamp, returns '' if no time component
  const getTimeFromTs = (ts: number | undefined): string => {
    if (!ts) return '';
    const d = new Date(ts);
    const h = d.getHours();
    const m = d.getMinutes();
    if (h === 0 && m === 0) return ''; // treat midnight as "no time set"
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // Helper: combine a date string (YYYY-MM-DD) and optional time (HH:mm) into a timestamp
  const combineDateAndTime = (dateStr: string, timeStr: string): number => {
    if (!dateStr) return 0;
    const [year, month, day] = dateStr.split('-').map(Number);
    let hours = 0, minutes = 0;
    if (timeStr) {
      const parts = timeStr.split(':').map(Number);
      hours = parts[0] || 0;
      minutes = parts[1] || 0;
    }
    return new Date(year, month - 1, day, hours, minutes).getTime();
  };

  // Helper: get date part from timestamp
  const getDateFromTs = (ts: number | undefined): string => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const handleTagAdd = (tag: string) => {
    if (!selectedNode || !tag.trim()) return;
    const newTags = [...selectedNode.tags, tag.trim()];
    const updatedNode = { ...selectedNode, tags: newTags };
    onUpdateNode(updatedNode);
  };

  const handleTagRemove = (index: number) => {
    if (!selectedNode) return;
    const newTags = selectedNode.tags.filter((_, i) => i !== index);
    const updatedNode = { ...selectedNode, tags: newTags };
    onUpdateNode(updatedNode);
  };

  const startEditingNotes = () => {
    setNotesContent(selectedNode?.notesMd || '');
    setIsEditingNotes(true);
  };

  const saveNotes = () => {
    if (selectedNode) {
      const updatedNode = { ...selectedNode, notesMd: notesContent };
      onUpdateNode(updatedNode);
    }
    setIsEditingNotes(false);
  };

  const startEditingEdgeNotes = () => {
    setEdgeNotesContent(selectedEdge?.notesMd || '');
    setIsEditingEdgeNotes(true);
  };

  const saveEdgeNotes = () => {
    if (selectedEdge) {
      const updatedEdge = { ...selectedEdge, notesMd: edgeNotesContent };
      onUpdateEdge(updatedEdge);
    }
    setIsEditingEdgeNotes(false);
  };

  const getFileIcon = (mime: string) => {
    if (mime.startsWith('image/')) return <Image size={16} />;
    if (mime.startsWith('video/')) return <Film size={16} />;
    return <FileText size={16} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!selectedNode && !selectedEdge) {
    return (
      <div
        className="
          w-56 border-l p-4 text-center transition-colors duration-200
          data-[theme=light]:bg-white data-[theme=light]:border-gray-300 data-[theme=light]:text-gray-600
          data-[theme=dark]:bg-gray-800 data-[theme=dark]:border-gray-700 data-[theme=dark]:text-gray-400
        "
        data-theme={theme}
      >
        Select an entity or edge to view properties
      </div>
    );
  }

  return (
    <>
      {/* Mobile Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${onClose ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      <div
        className="
          w-full h-full flex flex-col transition-all duration-300 ease-in-out border-l rounded-lg shadow-xl
          data-[theme=light]:bg-white data-[theme=light]:border-gray-300
          data-[theme=dark]:bg-gray-800 data-[theme=dark]:border-gray-700
          overflow-hidden
        "
        data-theme={theme}
      >
        {/* Header */}
        <div className={`
          flex items-center justify-between p-4 border-b transition-colors duration-200
          ${theme === 'light' ? 'border-gray-300' : 'border-gray-700'}
        `}>
          <h3 className={`
            font-medium transition-colors duration-200
            ${theme === 'light' ? 'text-gray-900' : 'text-white'}
          `}>
            {selectedNode ? 'Entity Properties' : 'Edge Properties'}
          </h3>
          <button
            onClick={onClose}
            className={`
              p-1 rounded transition-colors duration-200
              ${theme === 'light'
                ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
              }
            `}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        {selectedNode && (
          <div className={`
            flex border-b transition-colors duration-200
            ${theme === 'light' ? 'border-gray-300' : 'border-gray-700'}
          `}>
            {[
              { id: 'properties', label: 'Properties' },
              { id: 'attachments', label: `Files (${selectedNode.attachments.length})` },
              { id: 'notes', label: 'Notes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                flex-1 px-3 py-2 text-sm font-medium transition-colors duration-200
                ${activeTab === tab.id
                    ? `${theme === 'light'
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-blue-400 border-b-2 border-blue-400'
                    }`
                    : `${theme === 'light' ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-gray-300'}`
                  }
              `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-16">
          {selectedNode && activeTab === 'properties' && (
            <>
              {/* Label */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Label
                </label>
                <input
                  type="text"
                  value={selectedNode.label}
                  onChange={(e) => handleNodePropertyChange('label', e.target.value)}
                  className={`
                  w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                  ${theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900'
                      : 'bg-gray-700 border-gray-600 text-white'
                    }
                `}
                  placeholder="Enter label..."
                />
              </div>

              {/* Shape */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Entity Shape
                </label>
                <div className="flex gap-2">
                  {[
                    { id: 'rectangle', label: 'Rectangle' },
                    { id: 'circle', label: 'Circle' }
                  ].map((shape) => (
                    <button
                      key={shape.id}
                      onClick={() => handleNodePropertyChange('shape', shape.id)}
                      className={`
                        flex-1 px-3 py-2 text-sm border rounded transition-all duration-200
                        ${selectedNode.shape === shape.id || (!selectedNode.shape && shape.id === 'rectangle')
                          ? 'bg-blue-600 text-white border-blue-600'
                          : `${theme === 'light' ? 'bg-white border-gray-300 text-gray-700 hover:border-gray-400' : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'}`
                        }
                      `}
                    >
                      {shape.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size Presets */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Entity Size
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { id: 'small', label: 'S' },
                    { id: 'medium', label: 'M' },
                    { id: 'large', label: 'L' },
                    { id: 'custom', label: 'Custom' }
                  ].map((size) => {
                    const isCircle = selectedNode.shape === 'circle';
                    const presets = {
                      small: isCircle ? { w: 140, h: 140 } : { w: 220, h: 100 },
                      medium: isCircle ? { w: 220, h: 220 } : { w: 320, h: 160 },
                      large: isCircle ? { w: 320, h: 320 } : { w: 480, h: 280 }
                    };

                    const isSelected = size.id === 'custom'
                      ? !Object.values(presets).some(p => p.w === selectedNode.width && p.h === selectedNode.height)
                      : presets[size.id as keyof typeof presets].w === selectedNode.width && presets[size.id as keyof typeof presets].h === selectedNode.height;

                    return (
                      <button
                        key={size.id}
                        onClick={() => {
                          if (size.id === 'custom') return;
                          const p = presets[size.id as keyof typeof presets];
                          const updated = { ...selectedNode, width: p.w, height: p.h };
                          onUpdateNode(updated);
                        }}
                        disabled={size.id === 'custom'}
                        className={`
                          px-2 py-2 text-xs border rounded transition-all duration-200
                          ${isSelected
                            ? 'bg-blue-600 text-white border-blue-600'
                            : `${theme === 'light' ? 'bg-white border-gray-300 text-gray-700 hover:border-gray-400' : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'}`
                          }
                          ${size.id === 'custom' && !isSelected ? 'opacity-50 cursor-default' : ''}
                        `}
                      >
                        {size.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Resizable Toggle */}
              <div>
                <label className={`
                flex items-center gap-2 text-sm font-medium mb-4 cursor-pointer transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  <input
                    type="checkbox"
                    checked={!!selectedNode.isResizable}
                    onChange={(e) => {
                      const enabling = e.target.checked;
                      const updates: any = { isResizable: enabling };
                      // Set default dimensions when enabling resize if none exist
                      if (enabling && !selectedNode.width) {
                        const isCircle = selectedNode.shape === 'circle';
                        updates.width = isCircle ? 220 : 320;
                        updates.height = isCircle ? 220 : 160;
                      }
                      onUpdateNode({ ...selectedNode, ...updates });
                    }}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  Enable Custom Resizing
                </label>
              </div>

              {/* Type */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Type
                </label>
                <select
                  value={selectedNode.type}
                  onChange={(e) => handleNodePropertyChange('type', e.target.value)}
                  className={`
                  w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                  ${theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900'
                      : 'bg-gray-700 border-gray-600 text-white'
                    }
                `}
                >
                  {Object.entries(NODE_TYPES).map(([key, type]) => (
                    <option key={key} value={key}>{type.label}</option>
                  ))}
                </select>

                <div className="mt-3">
                  <label className={`
                    block text-xs mb-1 transition-colors duration-200
                    ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                  `}>Entity Type Name (Optional)</label>
                  <input
                    type="text"
                    value={selectedNode.customEntityType || ''}
                    onChange={(e) => handleNodePropertyChange('customEntityType', e.target.value)}
                    placeholder="e.g. Main Suspect"
                    className={`
                    w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                    ${theme === 'light'
                        ? 'bg-white border-gray-300 text-gray-900'
                        : 'bg-gray-700 border-gray-600 text-white'
                      }
                  `}
                  />
                </div>
              </div>

              {/* Color */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Color
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {[
                    '#3B82F6', // Blue
                    '#EF4444', // Red
                    '#10B981', // Green
                    '#F59E0B', // Yellow
                    '#8B5CF6', // Purple
                    '#EC4899', // Pink
                    '#06B6D4', // Cyan
                    '#84CC16', // Lime
                    '#F97316', // Orange
                    '#6B7280', // Gray
                    '#1F2937', // Dark Gray
                    '#FFFFFF'  // White
                  ].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleNodePropertyChange('color', color)}
                      className={`
                      w-6 h-6 rounded border-2 transition-all duration-200
                      ${selectedNode.color === color
                          ? `${theme === 'light' ? 'border-gray-900 scale-110' : 'border-white scale-110'}`
                          : `${theme === 'light'
                            ? 'border-gray-300 hover:border-gray-500'
                            : 'border-gray-600 hover:border-gray-400'
                          }`
                        }
                    `}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Background Color — only for sticky notes */}
              {(selectedNode.customEntityType === 'Note' || selectedNode.tags?.includes('note')) && (
                <div>
                  <label className={`
                    block text-sm font-medium mb-2 transition-colors duration-200
                    ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
                  `}>
                    Background Color
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {[
                      '#FEF3C7', // Yellow (default)
                      '#DBEAFE', // Blue
                      '#DCFCE7', // Green
                      '#FDE2E2', // Red/Pink
                      '#F3E8FF', // Purple
                      '#FFE4E6', // Rose
                      '#ECFDF5', // Emerald
                      '#FFF7ED', // Orange
                      '#F8FAFC', // Slate
                      '#FEFCE8', // Lime
                      '#FDF4FF', // Fuchsia
                      '#FFFFFF', // White
                    ].map((bgColor) => (
                      <button
                        key={bgColor}
                        onClick={() => handleNodePropertyChange('backgroundColor', bgColor)}
                        className={`
                          w-6 h-6 rounded border-2 transition-all duration-200
                          ${(selectedNode.backgroundColor || '#FEF3C7') === bgColor
                            ? `${theme === 'light' ? 'border-gray-900 scale-110' : 'border-white scale-110'}`
                            : `${theme === 'light'
                              ? 'border-gray-300 hover:border-gray-500'
                              : 'border-gray-600 hover:border-gray-400'
                            }`
                          }
                        `}
                        style={{ backgroundColor: bgColor }}
                        title={bgColor}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Tags
                </label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {selectedNode.tags.map((tag, index) => (
                      <span
                        key={index}
                        className={`
                        inline-flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors duration-200
                        ${theme === 'light' ? 'bg-gray-100 text-gray-700' : 'bg-gray-700 text-gray-300'}
                      `}
                      >
                        {tag}
                        <button
                          onClick={() => handleTagRemove(index)}
                          className={`
                          transition-colors duration-200
                          ${theme === 'light' ? 'text-gray-500 hover:text-red-600' : 'text-gray-400 hover:text-red-400'}
                        `}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    placeholder="Add tag and press Enter..."
                    className={`
                    w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                    ${theme === 'light'
                        ? 'bg-white border-gray-300 text-gray-900'
                        : 'bg-gray-700 border-gray-600 text-white'
                      }
                  `}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleTagAdd(e.currentTarget.value);
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                </div>
              </div>

              {/* Timeline */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Timeline
                </label>
                <div className="space-y-3">
                  {/* Start Date */}
                  <div>
                    <label className={`
                    block text-xs mb-1 transition-colors duration-200
                    ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                  `}>Start Date</label>
                    <input
                      type="date"
                      value={getDateFromTs(selectedNode.tsStart)}
                      onChange={(e) => {
                        const time = getTimeFromTs(selectedNode.tsStart);
                        handleNodePropertyChange('tsStart', e.target.value ? combineDateAndTime(e.target.value, time) : undefined);
                      }}
                      className={`
                      w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                      ${theme === 'light'
                          ? 'bg-white border-gray-300 text-gray-900'
                          : 'bg-gray-700 border-gray-600 text-white'
                        }
                    `}
                    />
                    {selectedNode.tsStart && (
                      <div className="mt-1">
                        <label className={`
                          block text-[10px] mb-0.5 transition-colors duration-200
                          ${theme === 'light' ? 'text-gray-500' : 'text-gray-500'}
                        `}>Time (24h, optional)</label>
                        <input
                          type="time"
                          value={getTimeFromTs(selectedNode.tsStart)}
                          onChange={(e) => {
                            const dateStr = getDateFromTs(selectedNode.tsStart);
                            handleNodePropertyChange('tsStart', combineDateAndTime(dateStr, e.target.value));
                          }}
                          className={`
                          w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                          ${theme === 'light'
                              ? 'bg-white border-gray-300 text-gray-900'
                              : 'bg-gray-700 border-gray-600 text-white'
                            }
                        `}
                        />
                      </div>
                    )}
                  </div>
                  {/* End Date */}
                  <div>
                    <label className={`
                    block text-xs mb-1 transition-colors duration-200
                    ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                  `}>End Date</label>
                    <input
                      type="date"
                      value={getDateFromTs(selectedNode.tsEnd)}
                      onChange={(e) => {
                        const time = getTimeFromTs(selectedNode.tsEnd);
                        handleNodePropertyChange('tsEnd', e.target.value ? combineDateAndTime(e.target.value, time) : undefined);
                      }}
                      className={`
                      w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                      ${theme === 'light'
                          ? 'bg-white border-gray-300 text-gray-900'
                          : 'bg-gray-700 border-gray-600 text-white'
                        }
                    `}
                    />
                    {selectedNode.tsEnd && (
                      <div className="mt-1">
                        <label className={`
                          block text-[10px] mb-0.5 transition-colors duration-200
                          ${theme === 'light' ? 'text-gray-500' : 'text-gray-500'}
                        `}>Time (24h, optional)</label>
                        <input
                          type="time"
                          value={getTimeFromTs(selectedNode.tsEnd)}
                          onChange={(e) => {
                            const dateStr = getDateFromTs(selectedNode.tsEnd);
                            handleNodePropertyChange('tsEnd', combineDateAndTime(dateStr, e.target.value));
                          }}
                          className={`
                          w-full px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                          ${theme === 'light'
                              ? 'bg-white border-gray-300 text-gray-900'
                              : 'bg-gray-700 border-gray-600 text-white'
                            }
                        `}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {selectedNode && activeTab === 'attachments' && (
            <>
              {/* Upload Area */}
              <div
                className={`
                relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200
                ${theme === 'light'
                    ? 'border-gray-300 hover:border-gray-400'
                    : 'border-gray-600 hover:border-gray-500'
                  }
                ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
                onDrop={(e) => {
                  if (isUploading) return;
                  e.preventDefault();
                  handleFileUpload(e.dataTransfer.files);
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 rounded-lg z-10">
                    <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                  </div>
                )}
                <Upload className={`
                mx-auto h-8 w-8 mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-500' : 'text-gray-400'}
              `} />
                <p className={`
                text-sm mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
              `}>
                  Drag files here or click to upload
                </p>
                <button
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Choose Files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  disabled={isUploading}
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />
              </div>

              {/* Attachments List */}
              <div className="space-y-2">
                {selectedNode.attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className={`
                    flex items-center gap-3 p-3 rounded border transition-colors duration-200
                    ${theme === 'light'
                        ? 'bg-gray-50 border-gray-200'
                        : 'bg-gray-700 border-gray-600'
                      }
                  `}
                  >
                    {attachment.mime.startsWith('image/') && (
                      <img
                        src={attachment.objectUrl}
                        alt={attachment.name}
                        className="w-10 h-10 object-cover rounded"
                      />
                    )}
                    {!attachment.mime.startsWith('image/') && (
                      <div className={`
                      w-10 h-10 rounded flex items-center justify-center transition-colors duration-200
                      ${theme === 'light' ? 'bg-gray-200 text-gray-600' : 'bg-gray-600 text-gray-400'}
                    `}>
                        {getFileIcon(attachment.mime)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`
                      text-sm truncate transition-colors duration-200
                      ${theme === 'light' ? 'text-gray-900' : 'text-white'}
                    `}>{attachment.name}</p>
                      <p className={`
                      text-xs transition-colors duration-200
                      ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                    `}>{formatFileSize(attachment.size)}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = attachment.objectUrl;
                          link.download = attachment.name;
                          link.click();
                        }}
                        className={`
                        p-1 rounded transition-colors duration-200
                        ${theme === 'light' ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900' : 'text-gray-400 hover:bg-gray-600 hover:text-white'}
                      `}
                        title="Download"
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => window.open(attachment.objectUrl, '_blank')}
                        className={`
                        p-1 rounded transition-colors duration-200
                        ${theme === 'light' ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900' : 'text-gray-400 hover:bg-gray-600 hover:text-white'}
                      `}
                        title="View"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        className={`
                        p-1 rounded transition-colors duration-200
                        ${theme === 'light' ? 'text-red-600 hover:bg-red-100 hover:text-red-700' : 'text-red-400 hover:bg-gray-600 hover:text-red-300'}
                      `}
                        title="Remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {selectedNode && activeTab === 'notes' && (
            <>
              <div className="flex items-center justify-between">
                <label className={`
                block text-sm font-medium transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Notes (Markdown)
                </label>
                <button
                  onClick={isEditingNotes ? saveNotes : startEditingNotes}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  {isEditingNotes ? 'Save' : 'Edit'}
                </button>
              </div>

              {isEditingNotes ? (
                <textarea
                  value={notesContent}
                  onChange={(e) => setNotesContent(e.target.value)}
                  className={`
                  w-full h-64 px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                  ${theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900'
                      : 'bg-gray-700 border-gray-600 text-white'
                    }
                `}
                  placeholder="Write your notes in Markdown..."
                />
              ) : (
                <div className={`
                prose max-w-none text-sm transition-colors duration-200
                ${theme === 'light' ? 'prose-gray' : 'prose-invert'}
              `}>
                  {selectedNode.notesMd ? (
                    <ReactMarkdown>{selectedNode.notesMd}</ReactMarkdown>
                  ) : (
                    <p className={`
                    italic transition-colors duration-200
                    ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                  `}>No notes yet. Click Edit to add some.</p>
                  )}
                </div>
              )}
            </>
          )}

          {selectedEdge && (
            <>
              {/* Edge Label */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Label
                </label>
                <input
                  type="text"
                  value={selectedEdge.label || ''}
                  onChange={(e) => handleEdgePropertyChange('label', e.target.value)}
                  className={`
                  w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                  ${theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900'
                      : 'bg-gray-700 border-gray-600 text-white'
                    }
                `}
                  placeholder="Enter relationship..."
                />
              </div>

              {/* Edge Color */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Color
                </label>
                <div className="grid grid-cols-6 gap-2">
                  {[
                    '#3B82F6', // Blue
                    '#EF4444', // Red
                    '#10B981', // Green
                    '#F59E0B', // Yellow
                    '#8B5CF6', // Purple
                    '#EC4899', // Pink
                    '#06B6D4', // Cyan
                    '#84CC16', // Lime
                    '#F97316', // Orange
                    '#6B7280', // Gray
                    '#1F2937', // Dark Gray
                    '#FFFFFF'  // White
                  ].map((color) => (
                    <button
                      key={color}
                      onClick={() => handleEdgePropertyChange('color', color)}
                      className={`
                      w-6 h-6 rounded border-2 transition-all duration-200
                      ${(selectedEdge.color || '#6B7280') === color
                          ? `${theme === 'light' ? 'border-gray-900 scale-110' : 'border-white scale-110'}`
                          : `${theme === 'light'
                            ? 'border-gray-300 hover:border-gray-500'
                            : 'border-gray-600 hover:border-gray-400'
                          }`
                        }
                    `}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              {/* Edge Timestamp */}
              <div>
                <label className={`
                block text-sm font-medium mb-2 transition-colors duration-200
                ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
              `}>
                  Date
                </label>
                <input
                  type="date"
                  value={getDateFromTs(selectedEdge.ts)}
                  onChange={(e) => {
                    const time = getTimeFromTs(selectedEdge.ts);
                    handleEdgePropertyChange('ts', e.target.value ? combineDateAndTime(e.target.value, time) : undefined);
                  }}
                  className={`
                  w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                  ${theme === 'light'
                      ? 'bg-white border-gray-300 text-gray-900'
                      : 'bg-gray-700 border-gray-600 text-white'
                    }
                `}
                />
                {selectedEdge.ts && (
                  <div className="mt-1">
                    <label className={`
                      block text-[10px] mb-0.5 transition-colors duration-200
                      ${theme === 'light' ? 'text-gray-500' : 'text-gray-500'}
                    `}>Time (24h, optional)</label>
                    <input
                      type="time"
                      value={getTimeFromTs(selectedEdge.ts)}
                      onChange={(e) => {
                        const dateStr = getDateFromTs(selectedEdge.ts);
                        handleEdgePropertyChange('ts', combineDateAndTime(dateStr, e.target.value));
                      }}
                      className={`
                      w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                      ${theme === 'light'
                          ? 'bg-white border-gray-300 text-gray-900'
                          : 'bg-gray-700 border-gray-600 text-white'
                        }
                    `}
                    />
                  </div>
                )}
              </div>

              {/* Edge Notes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={`
                  block text-sm font-medium transition-colors duration-200
                  ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}
                `}>
                    Notes (Markdown)
                  </label>
                  <button
                    onClick={isEditingEdgeNotes ? saveEdgeNotes : startEditingEdgeNotes}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    {isEditingEdgeNotes ? 'Save' : 'Edit'}
                  </button>
                </div>

                {isEditingEdgeNotes ? (
                  <textarea
                    value={edgeNotesContent}
                    onChange={(e) => setEdgeNotesContent(e.target.value)}
                    className={`
                    w-full h-32 px-3 py-2 border rounded resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                    ${theme === 'light'
                        ? 'bg-white border-gray-300 text-gray-900'
                        : 'bg-gray-700 border-gray-600 text-white'
                      }
                  `}
                    placeholder="Write your notes in Markdown..."
                  />
                ) : (
                  <div className={`
                  prose max-w-none text-sm transition-colors duration-200
                  ${theme === 'light' ? 'prose-gray' : 'prose-invert'}
                `}>
                    {selectedEdge.notesMd ? (
                      <ReactMarkdown>{selectedEdge.notesMd}</ReactMarkdown>
                    ) : (
                      <p className={`
                      italic transition-colors duration-200
                      ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                    `}>No notes yet. Click Edit to add some.</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
